import React, { useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  APIProvider,
  Map,
  useMap,
  AdvancedMarker,
} from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { Marker } from '@googlemaps/markerclusterer';
import { Stack, useTheme } from '@mui/material';

import { GoogleMapProps, StationPoint } from './types';
import {
  getAvailableCount,
  getMarkerColor,
  MARKER_COLOR_MAP,
} from './markerUtils';

const MapPin = ({
  fill,
  size = 40,
  selected = false,
}: {
  fill: string;
  size?: number;
  selected?: boolean;
}) => (
  <svg
    width={size}
    height={size * 1.25}
    viewBox="0 0 16 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      filter: selected
        ? 'drop-shadow(0 0 8px rgba(29,239,131,0.9))'
        : 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
      transform: selected ? 'scale(1.25)' : 'scale(1)',
      transformOrigin: 'center bottom',
      transition: 'transform 0.15s ease, filter 0.15s ease',
    }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.99999 20C8.42399 20 16 12.1078 16 7.79999C16 3.49217 12.4182 0 7.99999 0C3.58172 0 0 3.49217 0 7.79999C0 12.1078 7.57601 20 7.99999 20ZM8 11.7013C10.2401 11.7013 12.056 9.93071 12.056 7.74665C12.056 5.56258 10.2401 3.79205 8 3.79205C5.75994 3.79205 3.94401 5.56258 3.94401 7.74665C3.94401 9.93071 5.75994 11.7013 8 11.7013Z"
      fill={fill}
    />
  </svg>
);

const MapPinWithCount = ({
  fill,
  available,
  total,
  size = 40,
  selected = false,
}: {
  fill: string;
  available: number;
  total: number;
  size?: number;
  selected?: boolean;
}) => (
  <svg
    width={size}
    height={size * 1.25}
    viewBox="0 0 32 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      filter: selected
        ? 'drop-shadow(0 0 10px rgba(29,239,131,0.9))'
        : 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
      transform: selected ? 'scale(1.2)' : 'scale(1)',
      transformOrigin: 'center bottom',
      transition: 'transform 0.15s ease, filter 0.15s ease',
    }}
  >
    <path
      d="M16 40C16.848 40 32 24.2156 32 15.6C32 6.98434 24.8364 0 16 0C7.16344 0 0 6.98434 0 15.6C0 24.2156 15.152 40 16 40Z"
      fill={fill}
    />
    <text
      x="16"
      y="14"
      textAnchor="middle"
      fontFamily="system-ui, -apple-system, sans-serif"
      fontSize="12"
      fontWeight="700"
      fill="white"
    >
      {available}
    </text>
    <line
      x1="8"
      y1="17"
      x2="24"
      y2="17"
      stroke="white"
      strokeWidth="1.2"
    />
    <text
      x="16"
      y="27"
      textAnchor="middle"
      fontFamily="system-ui, -apple-system, sans-serif"
      fontSize="12"
      fontWeight="700"
      fill="white"
    >
      {total}
    </text>
  </svg>
);

type Point = google.maps.LatLngLiteral & { key: string; data: StationPoint };
const GOOGLE_MAP_API_KEY = import.meta.env.VITE_APP_GOOGLE_MAP_API_KEY;

const Markers = ({
  points,
  onMarkerClick,
  selectedKey,
}: {
  points: Point[];
  onMarkerClick?: (coords: google.maps.LatLngLiteral, key: string) => void;
  selectedKey?: string | null;
}) => {
  const theme = useTheme();
  const map = useMap();
  const clusterer = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<{ [key: string]: Marker }>({});

  useEffect(() => {
    if (!map) return;

    if (!clusterer.current) {
      class CustomRenderer extends google.maps.OverlayView {
        render({
          count,
          position,
        }: {
          count: number;
          position: google.maps.LatLng;
        }) {
          const element = document.createElement('div');
          element.style.width = '36px';
          element.style.height = '36px';
          element.style.borderRadius = '50%';
          element.style.backgroundColor = theme.palette.green.main;
          element.style.border = '2px solid white';
          element.style.display = 'flex';
          element.style.alignItems = 'center';
          element.style.justifyContent = 'center';
          element.style.color = 'white';
          element.style.fontSize = '13px';
          element.style.fontWeight = 'bold';
          element.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
          element.style.cursor = 'pointer';
          element.textContent = String(count);

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: element,
          });
        }
        draw() {}
        onAdd() {}
        onRemove() {}
      }
      clusterer.current = new MarkerClusterer({
        map,
        renderer: new CustomRenderer(),
        onClusterClick: (_event, cluster, clusterMap) => {
          if (cluster.bounds) {
            clusterMap.fitBounds(cluster.bounds, 64);
          }
        },
      });
    }

    return () => {
      if (clusterer.current) {
        clusterer.current.clearMarkers();
        clusterer.current = null;
      }
    };
  }, [map, theme.palette.green.main]);

  useEffect(() => {
    if (!clusterer.current) return;

    clusterer.current.clearMarkers();
    clusterer.current.addMarkers(Object.values(markersRef.current));
  }, [points]);

  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    if (marker) {
      markersRef.current[key] = marker;
    } else {
      delete markersRef.current[key];
    }
  }, []);

  return (
    <>
      {points.map((point) => {
        const connectors = point.data.connectors;
        const total = connectors?.length ?? 0;
        const fill =
          MARKER_COLOR_MAP[
            getMarkerColor(connectors, point.data.stationStatus)
          ];
        const isSelected = selectedKey === point.key;
        return (
          <AdvancedMarker
            position={point}
            key={point.key}
            zIndex={isSelected ? 999 : undefined}
            ref={(marker) => setMarkerRef(marker, point.key)}
            onClick={() => onMarkerClick && onMarkerClick(point, point.key)}
          >
            {total >= 2 ? (
              <div style={{ width: 36, height: 45 }}>
                <MapPinWithCount
                  size={36}
                  fill={fill}
                  available={getAvailableCount(connectors)}
                  total={total}
                  selected={isSelected}
                />
              </div>
            ) : (
              <div style={{ width: 24, height: 30 }}>
                <MapPin size={24} fill={fill} selected={isSelected} />
              </div>
            )}
          </AdvancedMarker>
        );
      })}
    </>
  );
};

interface MobileMapProps extends Omit<GoogleMapProps, 'onMarkerClick'> {
  onMarkerClick?: (coords: google.maps.LatLngLiteral, key: string) => void;
  selectedKey?: string | null;
  children?: ReactNode;
  fillContainer?: boolean;
}

export const MobileMap: React.FC<MobileMapProps> = ({
  stationPoints,
  defaultCenter,
  zoom = 13,
  onMarkerClick,
  mapHeight = '400px',
  selectedKey,
  children,
  fillContainer = false,
}) => {
  const points: Point[] = stationPoints
    .map((point, index) => ({
      lat: Number(point.geometry.coordinates[1]),
      lng: Number(point.geometry.coordinates[0]),
      key: `point-${index}`,
      data: point,
    }))
    .filter(
      (point) =>
        Number.isFinite(point.lat) &&
        Number.isFinite(point.lng) &&
        point.lat !== 0 &&
        point.lng !== 0,
    );

  return (
    <Stack
      style={{
        width: '100%',
        height: fillContainer ? '100%' : mapHeight,
        position: 'relative',
        transition: fillContainer ? undefined : 'height 0.5s ease',
      }}
    >
      <APIProvider apiKey={GOOGLE_MAP_API_KEY} libraries={['places']}>
        <MapContent
          points={points}
          defaultCenter={defaultCenter}
          zoom={zoom}
          onMarkerClick={onMarkerClick}
          mapHeight={fillContainer ? '100%' : mapHeight}
          selectedKey={selectedKey}
        >
          {children}
        </MapContent>
      </APIProvider>
    </Stack>
  );
};

const MapContent: React.FC<{
  points: Point[];
  defaultCenter: google.maps.LatLngLiteral;
  zoom: number;
  onMarkerClick?: (coords: google.maps.LatLngLiteral, key: string) => void;
  mapHeight: string;
  selectedKey?: string | null;
  children?: ReactNode;
}> = ({
  points,
  defaultCenter,
  zoom,
  onMarkerClick,
  mapHeight,
  selectedKey,
  children,
}) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      google.maps.event.trigger(map, 'resize');
    }
  }, [map, mapHeight]);

  useEffect(() => {
    if (map && defaultCenter) {
      map.panTo(defaultCenter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, defaultCenter.lat, defaultCenter.lng]);

  return (
    <Map
      defaultCenter={defaultCenter}
      defaultZoom={zoom}
      mapId="cd4d2cad27cc8f1f"
      colorScheme="DARK"
      disableDefaultUI={true}
      gestureHandling="greedy"
      style={{ width: '100%', height: '100%' }}
    >
      <Markers
        points={points}
        onMarkerClick={onMarkerClick}
        selectedKey={selectedKey}
      />
      {children}
    </Map>
  );
};
